import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ThemeProvider,
  createTheme,
  Box,
  Typography,
  Switch as MuiSwitch,
  FormControlLabel,
  MenuItem,
  Select as MuiSelect,
  FormControl,
  InputLabel,
  Chip,
  Divider,
} from "@mui/material";
import IconSelect from "./icon-select";
import { Edit, Trash2, Info, ChevronDown } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SingleSelect,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "lift-ui";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "lift-ui";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { sidebarApiClient, generateUUID } from "@lift-admin/utils";
import { apiClientDrawerV2 as apiClient } from '@lift-admin/utils';
import apiClientBrand from '@lift-admin/utils/apiClient';
import { Toastify } from 'lift-ui';
import {
  Button,
  DataTable,
  Badge,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { actionLog } from "../../../App";
import {
  buildDetails,
  buildSummary,
  diffFields,
  type FieldSpec,
} from "@lift-admin/utils/logChangeFormatter";
import BrandMultiSelect from "@/features/drawer-structure-v2/drawer-parts/brand-multiselect";
import OrganizationMultiSelect from "@/features/drawer-structure-v2/drawer-parts/organization-multiselect";
import {
  buildAccessLogChanges,
  inferNextAccessModeFromSignedIds,
} from "@/features/drawer-structure-v2/drawer-parts/accessLogHelpers";
import {
  buildDisplayPositionChange,
  formatOrderIndex,
} from "@/features/drawer-structure-v2/drawer-parts/displayPositionLogHelpers";
import type { Channel, ChannelUpsert, Application } from "@lift-admin/common";
import iconOptions from "../../../constants/iconOptions.json";

const theme = createTheme({
  palette: {
    primary: {
      main: "#16306D",
    },
    secondary: {
      main: "rgba(0, 0, 0, 0)",
    },
  },
  typography: {
    fontFamily: '"Figtree", sans-serif',
  },
});

interface ChannelsProps {
  toggle: string;
  searchText: string;
  openDialog: boolean;
  setOpenDialog: (value: boolean) => void;
  brands: any[];
  organizations: any[];
  hasEditAccess?: boolean;
  hasManageAccess?: boolean;
}

interface AddFormState {
  name: string;
  icon: string;
  application_id: string;
  display_order: number;
  is_active: boolean;
  /** null = inherit the application setting. See OMIT_ORG_BRAND_* helpers below. */
  omit_org_brand_from_url: boolean | null;
}

interface EditFormState {
  name: string;
  icon: string;
  application_id: string;
  display_order: number;
  is_active: boolean;
  /** null = inherit the application setting. See OMIT_ORG_BRAND_* helpers below. */
  omit_org_brand_from_url: boolean | null;
  brands_exclude: number[];
  organizations_exclude: number[];
}

/**
 * "Omit org/brand from URL" is a three-state override on a channel, not a
 * boolean. lift-teams only lets the channel win when the key is PRESENT, so:
 *   - key absent from the payload -> inherit the application value
 *   - true                        -> drop /:organizationName/:brandName
 *   - false                       -> keep the segments, overriding an app-level true
 *   - null                        -> clear an override already stored on the channel
 * A two-state switch cannot express this: sending `false` for a channel the
 * admin never touched silently defeats the application-level `true`.
 */
const OMIT_ORG_BRAND_INHERIT = "inherit";
const OMIT_ORG_BRAND_YES = "yes";
const OMIT_ORG_BRAND_NO = "no";

/** Radix Select needs a non-empty string, so the tri-state is encoded for the UI. */
const omitOrgBrandToSelectValue = (value: boolean | null | undefined): string => {
  if (value === true) return OMIT_ORG_BRAND_YES;
  if (value === false) return OMIT_ORG_BRAND_NO;
  return OMIT_ORG_BRAND_INHERIT;
};

const selectValueToOmitOrgBrand = (value: string): boolean | null => {
  if (value === OMIT_ORG_BRAND_YES) return true;
  if (value === OMIT_ORG_BRAND_NO) return false;
  return null;
};

/**
 * Read the stored override, falling back to the JSON column sidebar-v3 folds it
 * into. Absent stays null (Inherit) rather than collapsing to a misleading "off".
 */
const readStoredOmitOrgBrand = (data: any): boolean | null =>
  data?.omit_org_brand_from_url ?? data?.extra_metadata?.omit_org_brand_from_url ?? null;

/** Render the tri-state for the action log: Inherit / Yes / No, never yes-no. */
const formatOmitOrgBrand = (value: any): string => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "Inherit";
};

// Helper function to render status badge
const renderStatusBadge = (isActive: boolean) => {
  return (
    <Badge variant={isActive ? "default" : "secondary"}>
      {isActive ? "Active" : "Inactive"}
    </Badge>
  );
};

// Helper function to render brand badges
const renderBrandBadges = (data: any) => {
  if (
    !data ||
    !data.access?.brand_level ||
    !data.access.brand_level.brands ||
    !Array.isArray(data.access.brand_level.brands) ||
    data.access.brand_level.brands.length === 0
  ) {
    return <span className="text-gray-400">--</span>;
  }

  const brandIds = data.access.brand_level.brands;
  const access_type = data.access.brand_level.access_mode;

  const brandNames = brandIds.map((brand: any) => brand.name);

  return (
    <div className="flex flex-wrap gap-1 max-h-16 overflow-auto">
      {brandNames.map((name: string, index: number) => (
        <Badge
          key={index}
          variant={access_type === "EXCLUDE" ? "destructive" : "default"}
        >
          {name}
        </Badge>
      ))}
    </div>
  );
};

// Helper function to render organization badges
const renderOrganizationBadges = (data: any) => {
  if (
    !data ||
    !data.access?.organization_level ||
    !data.access.organization_level.organizations ||
    !Array.isArray(data.access.organization_level.organizations) ||
    data.access.organization_level.organizations.length === 0
  ) {
    return <span className="text-gray-400">--</span>;
  }

  const organizationIds = data.access.organization_level.organizations;
  const access_type = data.access.organization_level.access_mode;

  const organizationNames = organizationIds.map((org: any) => org.name);

  return (
    <div className="flex flex-wrap gap-1 max-h-16 overflow-auto">
      {organizationNames.map((name: string, index: number) => (
        <Badge
          key={index}
          variant={access_type === "EXCLUDE" ? "destructive" : "default"}
        >
          {name}
        </Badge>
      ))}
    </div>
  );
};

// Helper function to format date
const formatDate = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const Channels = ({
  toggle,
  searchText,
  openDialog,
  setOpenDialog,
  brands,
  organizations,
  hasEditAccess = true,
  hasManageAccess = true,
}: ChannelsProps) => {
  const showActionsColumn = hasEditAccess || hasManageAccess;
  const [loadData, setLoadData] = useState<boolean>(false);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [channelsBackup, setChannelsBackup] = useState<Channel[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [currentData, setCurrentData] = useState<Channel | null>(null);
  const [searchedResults, setSearchedResults] = useState<Channel[]>([]);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filter state (Application, Organization, Brand) - single select to match drawer items, reports, sandbox
  const [filterApplicationId, setFilterApplicationId] = useState<string>('');
  const [filterOrganizationId, setFilterOrganizationId] = useState<string>('');
  const [filterBrandId, setFilterBrandId] = useState<string>('');

  // Brands and organizations for filter dropdowns (loaded when tab is active; same API as default-sidebar-items)
  const [filterBrands, setFilterBrands] = useState<any[]>([]);
  const [filterOrganizations, setFilterOrganizations] = useState<any[]>([]);

  const [addFormState, setAddFormState] = useState<AddFormState>({
    name: "",
    icon: "",
    application_id: "",
    display_order: 0,
    is_active: true,
    omit_org_brand_from_url: null,
  });

  const [editFormState, setEditFormState] = useState<EditFormState>({
    name: "",
    icon: "",
    application_id: "",
    display_order: 0,
    is_active: true,
    omit_org_brand_from_url: null,
    brands_exclude: [],
    organizations_exclude: [],
  });

  const [makeItTop, setMakeItTop] = useState<boolean>(false);
  const [makeItBelow, setMakeItBelow] = useState<string>("");

  const getApplications = async () => {
    try {
      // Get user info from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const response = await apiClient.get('/admin/applications', {
        params: {
          include_inactive: true,
          user_id: user.id,
          is_admin: true,
          is_team: false,
        }
      });
      const applicationsData = response.data.sort((a: Application, b: Application) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setApplications(applicationsData);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      Toastify("error", "Failed to load applications");
    }
  };

  const getChannels = async () => {
    setLoadData(true);
    try {
      // Get user info from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const response = await apiClient.get('/admin/channels', {
        params: {
          include_inactive: true,
          user_id: user.id,
          is_admin: true,
          is_team: false,
        }
      });
      const channelsData = response.data.sort((a: Channel, b: Channel) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setChannels(channelsData);
      setChannelsBackup(channelsData);
    } catch (error) {
      console.error('Failed to fetch channels:', error);
      Toastify("error", "Failed to load channels");
    } finally {
      setLoadData(false);
    }
  };

  const loadFilterBrandsAndOrganizations = async () => {
    try {
      const brandsRes = await apiClientBrand.get('/brand/v1/admin/brands/all');
      setFilterBrands(brandsRes.data?.response || []);
      const orgsRes = await apiClientBrand.get('/brand/v1/organizations/all');
      setFilterOrganizations(orgsRes.data?.response || []);
    } catch (error) {
      console.error('Failed to load brands/organizations for filters:', error);
      setFilterBrands([]);
      setFilterOrganizations([]);
    }
  };

  useEffect(() => {
    if (toggle === "channels") {
      getApplications();
      getChannels();
      loadFilterBrandsAndOrganizations();
    }
  }, [toggle]);

  const rowData = useMemo(() => {
    let list = channels;
    if (filterApplicationId) {
      list = list.filter((c: any) => String(c.application_id) === filterApplicationId);
    }
    if (filterOrganizationId) {
      list = list.filter((c: any) => {
        const orgsList = (c as any).access?.organization_level?.organizations;
        if (!orgsList || !Array.isArray(orgsList)) return false;
        return orgsList.some((o: any) => String(o.id) === filterOrganizationId);
      });
    }
    if (filterBrandId) {
      list = list.filter((c: any) => {
        const brandsList = (c as any).access?.brand_level?.brands;
        if (!brandsList || !Array.isArray(brandsList)) return false;
        return brandsList.some((b: any) => String(b.id) === filterBrandId);
      });
    }
    return list;
  }, [channels, filterApplicationId, filterOrganizationId, filterBrandId]);

  // --- Progressive filter options ---
  // Application options: only apps that have at least one channel in the table
  const availableApplicationOptions = useMemo(() => {
    const appIds = new Set(channels.map((c: any) => String(c.application_id)).filter(Boolean));
    return applications
      .filter((a: any) => appIds.has(String(a.id)))
      .map((a: any) => ({ label: (a as any).display_name || a.name || String(a.id), value: String(a.id) }));
  }, [channels, applications]);

  // Channels visible after app filter only
  const channelsAfterAppFilter = useMemo(() => {
    if (!filterApplicationId) return channels;
    return channels.filter((c: any) => String(c.application_id) === filterApplicationId);
  }, [channels, filterApplicationId]);

  // Organization options: derived from channels visible after app filter
  const availableOrganizationOptions = useMemo(() => {
    const orgMap = new Map<string, string>();
    channelsAfterAppFilter.forEach((c: any) => {
      const orgsList: any[] = c.access?.organization_level?.organizations || [];
      orgsList.forEach((o: any) => orgMap.set(String(o.id), o.name ?? String(o.id)));
    });
    return Array.from(orgMap.entries()).map(([value, label]) => ({ label, value }));
  }, [channelsAfterAppFilter]);

  // Channels visible after app + org filters
  const channelsAfterAppOrgFilter = useMemo(() => {
    if (!filterOrganizationId) return channelsAfterAppFilter;
    return channelsAfterAppFilter.filter((c: any) => {
      const orgsList = c.access?.organization_level?.organizations;
      if (!orgsList || !Array.isArray(orgsList)) return false;
      return orgsList.some((o: any) => String(o.id) === filterOrganizationId);
    });
  }, [channelsAfterAppFilter, filterOrganizationId]);

  // Brand options: derived from channels visible after app + org filters
  const availableBrandOptions = useMemo(() => {
    const brandMap = new Map<string, string>();
    channelsAfterAppOrgFilter.forEach((c: any) => {
      const brandsList: any[] = c.access?.brand_level?.brands || [];
      brandsList.forEach((b: any) => brandMap.set(String(b.id), b.name ?? String(b.id)));
    });
    return Array.from(brandMap.entries()).map(([value, label]) => ({ label, value }));
  }, [channelsAfterAppOrgFilter]);

  useEffect(() => {
    setPage(0);
  }, [filterApplicationId, filterOrganizationId, filterBrandId]);

  // Get available channels for "Make it Below" dropdown
  const availableChannelsForBelow = useMemo(() => {
    const selectedApp = applications.find(app => app.id === editFormState.application_id) as any;
    if (!selectedApp || !selectedApp.children) return [];

    // Filter out the current editing channel and sort by display_order
    return selectedApp.children
      .filter((channel: any) => channel.id !== currentId)
  }, [applications, editFormState.application_id, currentId]);

  // Pagination calculations
  const paginatedData = useMemo(() => {
    return rowData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  }, [rowData, page, rowsPerPage]);

  const totalPages = Math.ceil(rowData.length / rowsPerPage);

  // Get application name by ID
  const getApplicationName = (appId: string) => {
    const app = applications.find(a => a.id === appId);
          return app ? app.name : 'Unknown Application';
  };

  const handleAddDialogClose = () => {
    setOpenDialog(false);
    setAddFormState({
      name: "",
      icon: "",
      application_id: "",
      display_order: 0,
      is_active: true,
      omit_org_brand_from_url: null,
    });
  };

  const resolveApplicationName = (id: any) =>
    applications.find((a: any) => String(a.id) === String(id))?.name ||
    (id ? String(id) : "Not Set");

  const channelFieldSpecs: FieldSpec[] = [
    { key: "name", label: "Name" },
    { key: "icon", label: "Icon" },
    {
      key: "application_id",
      label: "Application",
      resolve: resolveApplicationName,
    },
    {
      key: "display_order",
      label: "Display Order",
      type: "number",
      resolve: (order) => formatOrderIndex(order),
    },
    {
      key: "is_active",
      label: "Status",
      type: "boolean",
      boolStyle: "activeInactive",
    },
    {
      // Deliberately not type: "boolean" -- that would coerce Inherit (null /
      // absent) into "No", which is a different thing on the wire.
      key: "omit_org_brand_from_url",
      label: "Omit org/brand from URL",
      resolve: formatOmitOrgBrand,
    },
  ];

  const handleAddChannel = async () => {
    try {
      if (!addFormState.name.trim() || !addFormState.icon.trim() || !addFormState.application_id) {
        Toastify("error", "Please fill in all required fields");
        return;
      }

      const channelData: ChannelUpsert = {
        id: generateUUID(),
        name: addFormState.name.trim(),
        icon: addFormState.icon.trim(),
        application_id: addFormState.application_id,
        display_order: addFormState.display_order,
        is_active: addFormState.is_active,
      };

      // Inherit means the key must be absent from the body. A brand new channel
      // has no stored override to clear, so there is nothing to send at all.
      if (addFormState.omit_org_brand_from_url !== null) {
        channelData.omit_org_brand_from_url = addFormState.omit_org_brand_from_url;
      }

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await sidebarApiClient.post('/admin/channels', channelData, {
        params: {
          user_id: user?.id,
          brand_id: user?.brand_id,
          is_admin: true,
          is_team: false,
        },
      });

      const desc = {
        message: buildSummary("Channel", channelData.name, "created"),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "create_channel",
        channel_id: channelData.id,
        channel_name: channelData.name,
        user_id: user?.id,
        user_name: user?.name,
        details: buildDetails(
          {
            name: channelData.name,
            icon: channelData.icon,
            application_id: channelData.application_id,
            display_order: channelData.display_order,
            is_active: channelData.is_active,
            omit_org_brand_from_url: addFormState.omit_org_brand_from_url,
          },
          channelFieldSpecs,
          { skipEmpty: true }
        ),
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);

      Toastify("success", "Channel created successfully");
      getChannels();
      getApplications();
      handleAddDialogClose();
    } catch (error) {
      console.error('Failed to create channel:', error);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const errorDesc = {
        message: `Failed to create Channel (Channel Name: "${addFormState.name}") by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: (error as any).response?.status,
        method: "POST",
        url: "/api/admin/channels",
        category: "drawer",
        action: "create_channel",
        channel_name: addFormState.name,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
      Toastify("error", "Failed to create channel");
    }
  };

  const handleEditOpen = (data: Channel) => {
    setCurrentId(data.id);
    setCurrentData(data);
    console.log('data', data);

    let brands_exclude: number[] = [];
    let organizations_exclude: number[] = [];

    // Handle brand level access
    if ((data as any).access?.brand_level) {
      const brandLevel = (data as any).access.brand_level;
      if (brandLevel.brands && Array.isArray(brandLevel.brands)) {
        if (brandLevel.access_mode === "EXCLUDE") {
          // For exclude mode, store as negative IDs
          brands_exclude = brandLevel.brands.map((brand: any) => -parseInt(brand.id));
        } else if (brandLevel.access_mode === "INCLUDE") {
          // For include mode, store as positive IDs
          brands_exclude = brandLevel.brands.map((brand: any) => parseInt(brand.id));
        }
      }
    }

    // Handle organization level access
    if ((data as any).access?.organization_level) {
      const orgLevel = (data as any).access.organization_level;
      if (orgLevel.organizations && Array.isArray(orgLevel.organizations)) {
        if (orgLevel.access_mode === "EXCLUDE") {
          // For exclude mode, store as negative IDs
          organizations_exclude = orgLevel.organizations.map((org: any) => -parseInt(org.id));
        } else if (orgLevel.access_mode === "INCLUDE") {
          // For include mode, store as positive IDs
          organizations_exclude = orgLevel.organizations.map((org: any) => parseInt(org.id));
        }
      }
    }

    setEditFormState({
      name: data.name,
      icon: data.icon || "",
      application_id: data.application_id,
      display_order: data.display_order,
      is_active: data.is_active,
      omit_org_brand_from_url: readStoredOmitOrgBrand(data),
      brands_exclude,
      organizations_exclude,
    });

    // Set initial display order state
    setMakeItTop(data.display_order === 0);

    // If display_order is not 0, find the channel that's currently above this one
    let initialMakeItBelow = "";
    if (data.display_order !== 0) {
      const selectedApp = applications.find(app => app.id === data.application_id) as any;
      if (selectedApp && selectedApp.children) {
        // Filter and sort channels the same way as availableChannelsForBelow
        const availableChannels = selectedApp.children
          .filter((channel: any) => channel.id !== data.id)
          .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0));

        // Get all channels including current one, sorted by display_order
        const allChannelsInApp = selectedApp.children
          .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0));

        // Find current channel's position in sorted list
        const currentChannelIndex = allChannelsInApp.findIndex((channel: any) => channel.id === data.id);

        // If current channel is not at position 0, get the channel above it
        if (currentChannelIndex > 0) {
          const channelAbove = allChannelsInApp[currentChannelIndex - 1];
          // Make sure this channel exists in availableChannels
          if (availableChannels.find((ch: any) => ch.id === channelAbove.id)) {
            initialMakeItBelow = channelAbove.id;
          }
        }
      }
    }

    console.log('initialMakeItBelow', initialMakeItBelow);

    // Use setTimeout to ensure the state updates after the form state is set
    setTimeout(() => {
      setMakeItBelow(initialMakeItBelow);
    }, 0);

    setOpenEditDialog(true);
  };

  const handleEditDialogClose = () => {
    setOpenEditDialog(false);
    setCurrentData(null);
    setCurrentId(null);
    setEditFormState({
      name: "",
      icon: "",
      application_id: "",
      display_order: 0,
      is_active: true,
      omit_org_brand_from_url: null,
      brands_exclude: [],
      organizations_exclude: [],
    });
    setMakeItTop(false);
    setMakeItBelow("");
  };

  const handleEditChannel = async () => {
    if (!currentId || !currentData) {
      Toastify("error", "Invalid channel selected");
      return;
    }

    try {
      setLoadData(true);
      if (!editFormState.name.trim() || !editFormState.icon.trim() || !editFormState.application_id) {
        Toastify("error", "Please fill in all required fields");
        return;
      }

      // Validate display order selection
      if (!makeItTop && !makeItBelow) {
        Toastify("error", "Please either select 'Make it Top' or choose a channel in 'Make it Below'");
        return;
      }

      // // Calculate new display_order based on user selection
      // let newDisplayOrder = editFormState.display_order;

      // if (makeItTop) {
      //   newDisplayOrder = 0;
      // } else if (makeItBelow && availableChannelsForBelow.length > 0) {
      //   const selectedChannel = availableChannelsForBelow.find((channel: any) => channel.id === makeItBelow);
      //   if (selectedChannel) {
      //     newDisplayOrder = (selectedChannel.display_order || 0) + 1;
      //   }
      // }

      const storedOmitOrgBrand = readStoredOmitOrgBrand(currentData);

      const channelData: ChannelUpsert = {
        id: currentId,
        name: editFormState.name.trim(),
        icon: editFormState.icon.trim(),
        application_id: editFormState.application_id,
        display_order: editFormState.display_order,
        is_active: editFormState.is_active,
        created_at: currentData?.created_at,
        updated_at: new Date().toISOString(),
        below_item: makeItTop ? null : makeItBelow,
      };

      if (editFormState.omit_org_brand_from_url !== null) {
        // Yes / No: an explicit channel-level decision.
        channelData.omit_org_brand_from_url = editFormState.omit_org_brand_from_url;
      } else if (storedOmitOrgBrand !== null) {
        // Switched back to Inherit on a channel that already has an override
        // stored: send null so the server clears it.
        channelData.omit_org_brand_from_url = null;
      }
      // Otherwise Inherit was already the state: leave the key out entirely so
      // an unrelated edit (a rename, a reorder) cannot write a false that
      // overrides the application-level setting.

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await apiClient.post(`/admin/channels`, channelData, {
        params: {
          user_id: user?.id,
          brand_id: user?.brand_id,
          is_admin: true,
          is_team: false,
        },
      });

      // Check if brand_exclude is changed
      const originalBrands = (currentData as any)?.access?.brand_level?.brands || [];
      const originalBrandsExclude = originalBrands.map((brand: any) => {
        const accessMode = (currentData as any)?.access?.brand_level?.access_mode;
        return accessMode === "EXCLUDE" ? -parseInt(brand.id) : parseInt(brand.id);
      });

      const brandChanged = JSON.stringify(originalBrandsExclude.sort()) !== JSON.stringify(editFormState.brands_exclude.sort());

      if (brandChanged) {
        // Determine original access mode and brand IDs
        const originalAccessMode = (currentData as any)?.access?.brand_level?.access_mode || "INCLUDE";
        const originalBrandIds = originalBrandsExclude.map((id: number) => Math.abs(id).toString());

        // Determine new access mode and brand IDs from editFormState.brands_exclude
        const excludedBrands = editFormState.brands_exclude.filter((id: number) => id < 0).map((id: number) => Math.abs(id));
        const includedBrands = editFormState.brands_exclude.filter((id: number) => id > 0);

        let newAccessMode: "INCLUDE" | "EXCLUDE" = "INCLUDE";
        let newBrandIds: string[] = [];

        if (excludedBrands.length > 0) {
          newAccessMode = "EXCLUDE";
          newBrandIds = excludedBrands.map((id: number) => id.toString());
        } else if (includedBrands.length > 0) {
          newAccessMode = "INCLUDE";
          newBrandIds = includedBrands.map((id: number) => id.toString());
        }

        // Call brand-access API for each brand
        let brandAccessData: any[] = [];

        // Case 1: Access mode changed - need to remove all old entries and add all new entries
        if (originalAccessMode !== newAccessMode) {
          // Remove all brands from the old access mode
          for (const brandId of originalBrandIds) {
            let data = {
              brand_id: brandId,
              level_type: "channel",
              level_ids: [currentId],
              access_mode: originalAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "remove"
            };
            brandAccessData.push(data);
          }

          // Add all brands to the new access mode
          for (const brandId of newBrandIds) {
            let data = {
              brand_id: brandId,
              level_type: "channel",
              level_ids: [currentId],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "add"
            };
            brandAccessData.push(data);
          }
        } else {
          // Case 2: Same access mode - handle additions and removals
          const addedBrands = newBrandIds.filter((id: string) => !originalBrandIds.includes(id));
          const removedBrands = originalBrandIds.filter((id: string) => !newBrandIds.includes(id));

          // Add entries for added brands
          for (const brandId of addedBrands) {
            let data = {
              brand_id: brandId,
              level_type: "channel",
              level_ids: [currentId],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "add"
            };
            brandAccessData.push(data);
          }

          // Add entries for removed brands
          for (const brandId of removedBrands) {
            let data = {
              brand_id: brandId,
              level_type: "channel",
              level_ids: [currentId],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "remove"
            };
            brandAccessData.push(data);
          }
        }

        if (brandAccessData.length > 0) {
          try {
            console.log('brandAccessData', brandAccessData);
            let payload = {
              data: brandAccessData
            }
            await apiClient.post(`/admin/brand-access?user_id=${user.id}`, payload);
          } catch (brandAccessError) {
            console.error("Error updating brand access:", brandAccessError);
            Toastify("warning", "Brand access update failed");
          }
        }
      }

      // Check if organizations_exclude is changed
      const originalOrganizations = (currentData as any)?.access?.organization_level?.organizations || [];
      const originalOrganizationsExclude = originalOrganizations.map((org: any) => {
        const accessMode = (currentData as any)?.access?.organization_level?.access_mode;
        return accessMode === "EXCLUDE" ? -parseInt(org.id) : parseInt(org.id);
      });

      const organizationChanged = JSON.stringify(originalOrganizationsExclude.sort()) !== JSON.stringify(editFormState.organizations_exclude.sort());

      if (organizationChanged) {
        // Determine original access mode and organization IDs
        const originalAccessMode = (currentData as any)?.access?.organization_level?.access_mode || "INCLUDE";
        const originalOrganizationIds = originalOrganizationsExclude.map((id: number) => Math.abs(id).toString());

        // Determine new access mode and organization IDs from editFormState.organizations_exclude
        const excludedOrganizations = editFormState.organizations_exclude.filter((id: number) => id < 0).map((id: number) => Math.abs(id));
        const includedOrganizations = editFormState.organizations_exclude.filter((id: number) => id > 0);

        let newAccessMode: "INCLUDE" | "EXCLUDE" = "INCLUDE";
        let newOrganizationIds: string[] = [];

        if (excludedOrganizations.length > 0) {
          newAccessMode = "EXCLUDE";
          newOrganizationIds = excludedOrganizations.map((id: number) => id.toString());
        } else if (includedOrganizations.length > 0) {
          newAccessMode = "INCLUDE";
          newOrganizationIds = includedOrganizations.map((id: number) => id.toString());
        }

        // Call organization-access API for each organization
        let organizationAccessData: any[] = [];

        // Case 1: Access mode changed - need to remove all old entries and add all new entries
        if (originalAccessMode !== newAccessMode) {
          // Remove all organizations from the old access mode
          for (const organizationId of originalOrganizationIds) {
            let data = {
              organization_id: organizationId,
              level_type: "channel",
              level_ids: [currentId],
              access_mode: originalAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "remove"
            };
            organizationAccessData.push(data);
          }

          // Add all organizations to the new access mode
          for (const organizationId of newOrganizationIds) {
            let data = {
              organization_id: organizationId,
              level_type: "channel",
              level_ids: [currentId],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "add"
            };
            organizationAccessData.push(data);
          }
        } else {
          // Case 2: Same access mode - handle additions and removals
          const addedOrganizations = newOrganizationIds.filter((id: string) => !originalOrganizationIds.includes(id));
          const removedOrganizations = originalOrganizationIds.filter((id: string) => !newOrganizationIds.includes(id));

          // Add entries for added organizations
          for (const organizationId of addedOrganizations) {
            let data = {
              organization_id: organizationId,
              level_type: "channel",
              level_ids: [currentId],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "add"
            };
            organizationAccessData.push(data);
          }

          // Add entries for removed organizations
          for (const organizationId of removedOrganizations) {
            let data = {
              organization_id: organizationId,
              level_type: "channel",
              level_ids: [currentId],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "remove"
            };
            organizationAccessData.push(data);
          }
        }

        if (organizationAccessData.length > 0) {
          try {
            console.log('organizationAccessData', organizationAccessData);
            let payload = {
              data: organizationAccessData
            }
            await apiClient.post(`/admin/organization-access?user_id=${user.id}`, payload);
          } catch (organizationAccessError) {
            console.error("Error updating organization access:", organizationAccessError);
            Toastify("warning", "Organization access update failed");
          }
        }
      }

      // Build before -> after changes for the log.
      // Display position is logged separately (Make it Top / Make it Below).
      const previousChannel = {
        name: (currentData as any)?.name,
        icon: (currentData as any)?.icon,
        application_id: (currentData as any)?.application_id,
        is_active: (currentData as any)?.is_active,
        omit_org_brand_from_url: storedOmitOrgBrand,
      };
      const nextChannel = {
        name: channelData.name,
        icon: channelData.icon,
        application_id: channelData.application_id,
        is_active: channelData.is_active,
        omit_org_brand_from_url: editFormState.omit_org_brand_from_url,
      };
      const changes = diffFields(
        previousChannel,
        nextChannel,
        channelFieldSpecs.filter((spec) => spec.key !== "display_order")
      );

      let nextDisplayOrder = Number(editFormState.display_order) || 0;
      if (makeItTop) {
        nextDisplayOrder = 0;
      } else if (makeItBelow && availableChannelsForBelow.length > 0) {
        const selectedChannelIndex = availableChannelsForBelow.findIndex(
          (channel: any) => channel.id === makeItBelow
        );
        const selectedChannel = availableChannelsForBelow.find(
          (channel: any) => channel.id === makeItBelow
        );
        if (selectedChannelIndex !== -1) {
          nextDisplayOrder = selectedChannelIndex + 1;
        } else if (selectedChannel) {
          nextDisplayOrder = (selectedChannel.display_order || 0) + 1;
        }
      }

      const selectedApp = applications.find(
        (app) => app.id === editFormState.application_id
      ) as any;
      const channelPeers = selectedApp?.children || availableChannelsForBelow;
      const positionChange = buildDisplayPositionChange({
        previousOrder: Number((currentData as any)?.display_order) || 0,
        nextOrder: nextDisplayOrder,
        nextMakeItTop: makeItTop,
        nextBelowItemId: makeItBelow,
        peers: channelPeers,
        currentId,
        peerLabel: "Channel",
      });
      if (positionChange) changes.push(positionChange);

      // Prefer names from access payloads + loaded brand/org lists
      const brandLookup = [
        ...(brands || []),
        ...(originalBrands || []),
      ];
      const orgLookup = [
        ...(organizations || []),
        ...(originalOrganizations || []),
      ];

      if (brandChanged) {
        changes.push(
          ...buildAccessLogChanges(
            "Brand",
            originalBrandsExclude,
            editFormState.brands_exclude,
            brandLookup,
            {
              previousMode: (currentData as any)?.access?.brand_level
                ?.access_mode,
              nextMode: inferNextAccessModeFromSignedIds(
                editFormState.brands_exclude,
                (currentData as any)?.access?.brand_level?.access_mode
              ),
            }
          )
        );
      }
      if (organizationChanged) {
        changes.push(
          ...buildAccessLogChanges(
            "Organization",
            originalOrganizationsExclude,
            editFormState.organizations_exclude,
            orgLookup,
            {
              previousMode: (currentData as any)?.access?.organization_level
                ?.access_mode,
              nextMode: inferNextAccessModeFromSignedIds(
                editFormState.organizations_exclude,
                (currentData as any)?.access?.organization_level?.access_mode
              ),
            }
          )
        );
      }
      const desc = {
        message: buildSummary(
          "Channel",
          channelData.name,
          "updated",
          changes.length
        ),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "update_channel",
        channel_id: currentId,
        channel_name: channelData.name,
        changes,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);

      Toastify("success", "Channel updated successfully");
      getChannels();
      getApplications();
      handleEditDialogClose();
    } catch (error: any) {
      console.error('Failed to update channel:', error);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const errorDesc = {
        message: `Failed to update Channel (Channel ID: ${currentId}, Channel Name: "${editFormState.name}") by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: error.response?.status,
        method: "POST",
        url: `/admin/channels`,
        category: "drawer",
        action: "update_channel",
        channel_id: currentId,
        channel_name: editFormState.name,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
      if (error.response?.data?.detail) {
        const errorMessages = Array.isArray(error.response.data.detail) ? error.response.data.detail.map((error: any) => `${error.loc?.join(".")}: ${error.msg}`).join(", ") : error.response.data.detail;
        Toastify("error", errorMessages);
      } else {
        Toastify("error", "Failed to update channel");
      }
    } finally {
      setLoadData(false);
    }
  };

  const handleDeleteOpen = (data: Channel) => {
    setCurrentId(data.id);
    setCurrentData(data);
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
    setCurrentData(null);
    setCurrentId(null);
  };

  const handleDeleteChannel = async () => {
    if (!currentId || !currentData) {
      Toastify("error", "Invalid channel selected");
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await apiClient.delete(`/admin/channels/${currentId}`, {
        params: {
          user_id: user?.id,
          channel_id: currentId,
          brand_id: user?.brand_id,
          is_admin: true,
          is_team: false,
        },
      });

      const desc = {
        message: buildSummary("Channel", currentData.name, "deleted"),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "delete_channel",
        channel_id: currentId,
        channel_name: currentData.name,
        user_id: user?.id,
        user_name: user?.name,
        details: buildDetails(
          {
            name: currentData.name,
            icon: currentData.icon,
            application_id: currentData.application_id,
            display_order: currentData.display_order,
            is_active: currentData.is_active,
          },
          channelFieldSpecs,
          { skipEmpty: true }
        ),
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);

      Toastify("success", "Channel deleted successfully");
      getChannels();
      getApplications();
      handleDeleteDialogClose();
    } catch (error) {
      console.error('Failed to delete channel:', error);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const errorDesc = {
        message: `Failed to delete Channel (Channel ID: ${currentId}, Channel Name: "${currentData.name}") by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: (error as any).response?.status,
        method: "DELETE",
        url: `/admin/channels/${currentId}`,
        category: "drawer",
        action: "delete_channel",
        channel_id: currentId,
        channel_name: currentData.name,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
      Toastify("error", "Failed to delete channel");
    }
  };

  const searchResults = (query: string) => {
    const filteredChannels = [...channelsBackup];
    const results = filteredChannels.filter((channel) =>
      channel.name?.toLowerCase().includes(query.toLowerCase()) ||
      channel.icon?.toLowerCase().includes(query.toLowerCase())
    );
    setChannels(results);
  };

  useEffect(() => {
    searchResults(searchText);
  }, [searchText, channelsBackup]);

  // Reset "Make it Below" selection when application changes
  useEffect(() => {
    setMakeItBelow("");
  }, [editFormState.application_id]);

  // Reset "Make it Below" when "Make it Top" is enabled
  useEffect(() => {
    if (makeItTop) {
      setMakeItBelow("");
    }
  }, [makeItTop]);

  // Automatically select "Make it Top" when application has no other channels
  useEffect(() => {
    if (availableChannelsForBelow.length === 0 && !makeItTop) {
      setMakeItTop(true);
      setMakeItBelow("");
    }
  }, [availableChannelsForBelow.length, makeItTop]);

  return (
    <>
      <ThemeProvider theme={theme}>
        <div className="flex flex-col h-full">
          <p className="text-sm text-muted-foreground mb-3">
            Channels are main sections within an application&apos;s sidebar.
          </p>
          <div className="flex flex-wrap items-end gap-3 mb-3 flex-shrink-0">
            <div className="space-y-1.5 min-w-[160px]">
              <Label htmlFor="filter-application" className="text-sm font-medium">
                Application
              </Label>
              <SingleSelect
                options={[
                  { label: "All applications", value: "all" },
                  ...availableApplicationOptions,
                ]}
                defaultValue={filterApplicationId || "all"}
                onChange={(value) => {
                  const val = value || "all";
                  setFilterApplicationId(val === "all" ? "" : val);
                  setFilterOrganizationId("");
                  setFilterBrandId("");
                }}
                placeholder="All applications"
                containerClassName="w-full"
              />
            </div>
            <div className="space-y-1.5 min-w-[160px]">
              <div className="flex items-center gap-1">
                <Label htmlFor="filter-organization" className="text-sm font-medium">
                  Organization
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        className="h-3.5 w-3.5 text-muted-foreground cursor-default"
                        aria-hidden="true"
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <span className="max-w-xs text-xs">
                        Filters channels by organizations that are included or excluded in their access settings.
                      </span>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <SingleSelect
                options={[
                  { label: "All organizations", value: "all" },
                  ...availableOrganizationOptions,
                ]}
                defaultValue={filterOrganizationId || "all"}
                onChange={(value) => {
                  const val = value || "all";
                  setFilterOrganizationId(val === "all" ? "" : val);
                  setFilterBrandId("");
                }}
                placeholder="All organizations"
                containerClassName="w-full"
              />
            </div>
            <div className="space-y-1.5 min-w-[160px]">
              <div className="flex items-center gap-1">
                <Label htmlFor="filter-brand" className="text-sm font-medium">
                  Brand
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        className="h-3.5 w-3.5 text-muted-foreground cursor-default"
                        aria-hidden="true"
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <span className="max-w-xs text-xs">
                        Filters channels by brands that are included or excluded in their access settings.
                      </span>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <SingleSelect
                options={[
                  { label: "All brands", value: "all" },
                  ...availableBrandOptions,
                ]}
                defaultValue={filterBrandId || "all"}
                onChange={(value) => {
                  const val = value || "all";
                  setFilterBrandId(val === "all" ? "" : val);
                }}
                placeholder="All brands"
                containerClassName="w-full"
              />
            </div>
          </div>
          <div className="flex-1 min-h-0 flex flex-col">
            <DataTable
              className="flex-1 min-h-0 overflow-auto"
              maxHeight="100%"
              columns={(() => {
                const orderLabels = ["first", "second", "third", "fourth", "fifth", "sixth", "seventh", "eighth", "ninth", "tenth"] as const;
                const getOrderLabel = (n: number) => { const i = Number(n); if (i >= 0 && i < orderLabels.length) return orderLabels[i]; return `${(isNaN(i) ? 0 : i) + 1}th`; };
                const cols: TableColumn<any>[] = [
                  { key: "application", header: "Application", accessor: (row: any) => getApplicationName(row.application_id), width: "160px", render: (_v: any, row: any) => <span className="text-gray-600">{getApplicationName(row.application_id)}</span> },
                  { key: "name", header: "Channel Name", accessor: "name", width: "180px", render: (_v: any, row: any) => <span className="font-medium">{row.name}</span> },
                  { key: "icon", header: "Icon", accessor: "icon", width: "100px", render: (_v: any, row: any) => row.icon && (row.icon.startsWith("http") || row.icon.startsWith("/")) ? <img src={row.icon} alt={row.name || "icon"} className="w-6 h-6 object-contain inline-block" /> : <span className="text-gray-600">{row.icon || "--"}</span> },
                  { key: "organizations", header: "Excluded/Include Organizations", accessor: (row: any) => row.id, width: "200px", render: (_v: any, row: any) => renderOrganizationBadges(row) },
                  { key: "brands", header: "Excluded/Include Brands", accessor: (row: any) => row.id, width: "200px", render: (_v: any, row: any) => renderBrandBadges(row) },
                  { key: "display_order", header: "Order", accessor: "display_order", width: "100px", align: "center", render: (_v: any, row: any) => <span className="text-gray-600">{getOrderLabel(Number(row.display_order) ?? 0)}</span> },
                  { key: "status", header: "Status", accessor: (row: any) => row.is_active, width: "100px", align: "center", render: (_v: any, row: any) => renderStatusBadge(row.is_active) },
                  { key: "created_at", header: "Created", accessor: (row: any) => formatDate(row.created_at), width: "120px", render: (_v: any, row: any) => <span className="text-gray-600">{formatDate(row.created_at)}</span> },
                ];
                if (showActionsColumn) {
                  cols.push({
                    key: "actions",
                    header: "Actions",
                    accessor: (row: any) => row.id,
                    width: "100px",
                    align: "center",
                    render: (_v: any, row: any) => (
                      <div className="flex gap-1 justify-center">
                        {hasEditAccess && (
                          <Button variant="ghost" size="sm" onClick={() => handleEditOpen(row)} className="h-8 w-8 p-0">
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ),
                  });
                }
                return cols;
              })()}
              data={paginatedData}
              loading={loadData}
              emptyMessage="No channels found."
              stickyHeader
              showActions={false}
              pagination={{
                currentPage: page + 1,
                totalPages: Math.max(1, totalPages),
                pageSize: rowsPerPage,
                totalItems: rowData.length,
                onPageChange: (p) => setPage(p - 1),
                onPageSizeChange: (size) => { setRowsPerPage(Number(size)); setPage(0); },
                pageSizeOptions: [10, 20, 50, 100],
              }}
            />
          </div>
        </div>

        {/* Add Channel Sheet */}
        <Sheet open={openDialog} onOpenChange={(open) => !open && handleAddDialogClose()}>
          <SheetContent
            className="w-full sm:max-w-2xl flex flex-col h-full"
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <SheetHeader>
              <SheetTitle>Add New Channel</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto min-h-0">
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="add-channel-name">Channel Name *</Label>
                <Input
                  id="add-channel-name"
              value={addFormState.name}
              onChange={(e) =>
                setAddFormState((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
                  placeholder="Enter channel name"
            />
              </div>

              <div className="space-y-2">
                <Label>Icon *</Label>
                <IconSelect
                  options={iconOptions}
                  value={addFormState.icon || null}
                  onChange={(val) =>
                    setAddFormState((prev) => ({ ...prev, icon: val ?? "" }))
                  }
                  placeholder="e.g., dashboard, analytics, settings"
                />
              </div>

              <div className="space-y-2">
                <Label>Application *</Label>
              <Select
                value={addFormState.application_id}
                  onValueChange={(value) =>
                  setAddFormState((prev) => ({
                    ...prev,
                      application_id: value,
                  }))
                }
              >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select application" />
                  </SelectTrigger>
                  <SelectContent>
                {applications.map((app) => (
                      <SelectItem key={app.id} value={app.id}>
                    {app.name}
                      </SelectItem>
                ))}
                  </SelectContent>
              </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="add-channel-omit-org-brand">Omit org/brand from URL</Label>
                <Select
                  value={omitOrgBrandToSelectValue(addFormState.omit_org_brand_from_url)}
                  onValueChange={(value) =>
                    setAddFormState((prev) => ({
                      ...prev,
                      omit_org_brand_from_url: selectValueToOmitOrgBrand(value),
                    }))
                  }
                >
                  <SelectTrigger id="add-channel-omit-org-brand" className="w-full">
                    <SelectValue placeholder="Inherit (default)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={OMIT_ORG_BRAND_INHERIT}>Inherit (default)</SelectItem>
                    <SelectItem value={OMIT_ORG_BRAND_YES}>Yes</SelectItem>
                    <SelectItem value={OMIT_ORG_BRAND_NO}>No</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Inherit follows the application setting. Yes drops the org/brand
                  segments; No keeps them even when the application omits them.
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="add-channel-active"
                  checked={addFormState.is_active}
                  onCheckedChange={(checked) =>
                    setAddFormState((prev) => ({
                      ...prev,
                      is_active: checked,
                    }))
                  }
                />
                <Label htmlFor="add-channel-active">Active</Label>
              </div>
            </div>
            </div>
            <SheetFooter className="flex gap-2 pt-4 border-t flex-shrink-0 mt-auto">
              <Button onClick={handleAddDialogClose} variant="outline">
              Cancel
            </Button>
            <Button
              onClick={handleAddChannel}
              disabled={!addFormState.name.trim() || !addFormState.icon.trim() || !addFormState.application_id}
            >
              Create
            </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Edit Channel Sheet */}
        <Sheet open={openEditDialog} onOpenChange={(open) => !open && handleEditDialogClose()}>
          <SheetContent
            className="w-full sm:max-w-2xl flex flex-col h-full"
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <SheetHeader>
              <SheetTitle>Edit Channel</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto min-h-0">
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-channel-name">Channel Name *</Label>
                <Input
                  id="edit-channel-name"
              value={editFormState.name}
              onChange={(e) =>
                setEditFormState((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
                  placeholder="Enter channel name"
            />
              </div>

              <div className="space-y-2">
                <Label>Icon *</Label>
                <IconSelect
                  options={iconOptions}
                  value={editFormState.icon || null}
                  onChange={(val) =>
                    setEditFormState((prev) => ({ ...prev, icon: val ?? "" }))
                  }
                  placeholder="e.g., dashboard, analytics, settings"
                />
              </div>

              <div className="space-y-2">
                <Label>Application *</Label>
              <Select
                value={editFormState.application_id}
                  onValueChange={(value) =>
                  setEditFormState((prev) => ({
                    ...prev,
                      application_id: value,
                  }))
                }
              >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select application" />
                  </SelectTrigger>
                  <SelectContent>
                {applications.map((app) => (
                      <SelectItem key={app.id} value={app.id}>
                    {app.name}
                      </SelectItem>
                ))}
                  </SelectContent>
              </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-channel-omit-org-brand">Omit org/brand from URL</Label>
                <Select
                  value={omitOrgBrandToSelectValue(editFormState.omit_org_brand_from_url)}
                  onValueChange={(value) =>
                    setEditFormState((prev) => ({
                      ...prev,
                      omit_org_brand_from_url: selectValueToOmitOrgBrand(value),
                    }))
                  }
                >
                  <SelectTrigger id="edit-channel-omit-org-brand" className="w-full">
                    <SelectValue placeholder="Inherit (default)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={OMIT_ORG_BRAND_INHERIT}>Inherit (default)</SelectItem>
                    <SelectItem value={OMIT_ORG_BRAND_YES}>Yes</SelectItem>
                    <SelectItem value={OMIT_ORG_BRAND_NO}>No</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Inherit follows the application setting. Yes drops the org/brand
                  segments; No keeps them even when the application omits them.
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-channel-active"
                  checked={editFormState.is_active}
                  onCheckedChange={(checked) =>
                    setEditFormState((prev) => ({
                      ...prev,
                      is_active: checked,
                    }))
                  }
                />
                <Label htmlFor="edit-channel-active">Active</Label>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-2">Display Order</h3>
                <div className="flex items-start gap-2 text-sm text-muted-foreground mb-4">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p>
              Control the position of this channel in the sidebar. Enable "Make it Top" to move to position top of the application, or select another channel to position below. Order is 0 based indexing.
                  </p>
                </div>

                <div className="flex items-center space-x-2 mb-4">
                <Switch
                    id="edit-channel-make-top"
                  checked={makeItTop}
                    onCheckedChange={(checked) => setMakeItTop(checked)}
              disabled={availableChannelsForBelow.length === 0}
            />
                  <Label htmlFor="edit-channel-make-top">Move to Top</Label>
                </div>

            {makeItTop && availableChannelsForBelow.length === 0 && (
                  <div className="flex items-start gap-2 text-sm text-muted-foreground mb-4">
                    <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <p>No other channels are present in this application.</p>
                  </div>
            )}

            {!makeItTop && (
                  <div className="space-y-2 mb-4">
                    <Label>Make it Below</Label>
                <Select
                  value={makeItBelow}
                      onValueChange={(value) => setMakeItBelow(value)}
                  disabled={availableChannelsForBelow.length === 0}
                >
                      <SelectTrigger className={!makeItBelow ? "border-red-500" : ""}>
                        <SelectValue placeholder="Select channel" />
                      </SelectTrigger>
                      <SelectContent>
                  {availableChannelsForBelow.length === 0 ? (
                          <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      No other channels available in this application
                          </div>
                  ) : (
                          availableChannelsForBelow.map((channel: any) => (
                            <SelectItem key={channel.id} value={channel.id}>
                        {channel.name}
                            </SelectItem>
                    ))
                  )}
                      </SelectContent>
                </Select>
                  </div>
            )}

                <p className="text-sm text-muted-foreground">
              {(() => {
                if (makeItTop) {
                      return <><strong>Current Position:</strong> Top (Order: 0)</>;
                } else if (makeItBelow) {
                  const selectedChannelIndex = availableChannelsForBelow.findIndex((channel: any) => channel.id === makeItBelow);
                  const selectedChannel = availableChannelsForBelow.find((channel: any) => channel.id === makeItBelow);
                  const channelName = selectedChannel ? selectedChannel.name : makeItBelow;
                  const newOrder = selectedChannelIndex !== -1 ? selectedChannelIndex + 1 : editFormState.display_order;
                      return <><strong>Current Position:</strong> Below "{channelName}" (Order: {newOrder})</>;
                } else {
                      return <><strong>Current Position:</strong> Not Set</>;
                }
              })()}
                </p>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-4">Manage Access</h3>

                <Collapsible className="mb-6">
                  <CollapsibleTrigger className="flex items-center justify-between w-full rounded-lg border border-input bg-background px-4 py-3 text-left font-medium hover:bg-accent/50 h-10 [&[data-state=open]>svg]:rotate-180">
                    <span>Brand Access</span>
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <p className="text-sm text-muted-foreground flex items-center mb-3">
                      <Info className="h-4 w-4 shrink-0 mr-1" />
                      Use negative IDs (e.g., -1, -2) to exclude specific brands. Use positive IDs (e.g., 1, 2) to include only those specific brands.
                    </p>
                    <BrandMultiSelect
                      defaultBrands={editFormState.brands_exclude}
                      setDefaultBrands={(field: string, value: number[]) => {
                        setEditFormState((prev) => ({
                          ...prev,
                          brands_exclude: value,
                        }));
                      }}
                    />
                  </CollapsibleContent>
                </Collapsible>

                <Collapsible className="mb-8">
                  <CollapsibleTrigger className="flex items-center justify-between w-full rounded-lg border border-input bg-background px-4 py-3 text-left font-medium hover:bg-accent/50 h-10 [&[data-state=open]>svg]:rotate-180">
                    <span>Organization Access</span>
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <p className="text-sm text-muted-foreground flex items-center mb-3">
                      <Info className="h-4 w-4 shrink-0 mr-1" />
                      Use negative IDs (e.g., -1, -2) to exclude specific organizations. Use positive IDs (e.g., 1, 2) to include only those specific organizations.
                    </p>
                    <OrganizationMultiSelect
                      defaultOrganizations={editFormState.organizations_exclude}
                      setDefaultOrganizations={(field: string, value: number[]) => {
                        setEditFormState((prev) => ({
                          ...prev,
                          organizations_exclude: value,
                        }));
                      }}
                    />
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>
            </div>
            <SheetFooter className="flex gap-2 pt-4 border-t flex-shrink-0 mt-auto">
              <Button onClick={handleEditDialogClose} variant="outline">
              Cancel
            </Button>
            <Button
              onClick={handleEditChannel}
              disabled={
                !editFormState.name.trim() ||
                !editFormState.icon.trim() ||
                !editFormState.application_id ||
                (!makeItTop && !makeItBelow) ||
                loadData
              }
            >
              {loadData ? 'Updating...' : 'Update'}
            </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Delete Channel Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={handleDeleteDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Channel</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the channel "{currentData?.name}"?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteDialogClose} color="primary" variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteChannel}
              color="error"
              disabled={true}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </>
  );
};

export default Channels;
